import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, FAQ, Breadcrumbs, CTABand } from "@/components/site/Blocks";

import { comparisons, findComparison } from "@/data/comparisons";
import { buildHead } from "@/lib/seo-head";
import { FannieRentalIncomeUpdateCard } from "@/components/site/FannieRentalIncomeUpdate";

export const Route = createFileRoute("/compare/$slug")({
  loader: ({ params }) => {
    const c = findComparison(params.slug);
    if (!c) throw notFound();
    return { comparison: c };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Comparison not found" }, { name: "robots", content: "noindex" }] };
    const c = loaderData.comparison;
    const url = `/compare/${params.slug}`;
    return buildHead({
      title: c.metaTitle,
      description: c.metaDesc,
      url,
      keywords: `${c.aName} vs ${c.bName}, ${c.aName} vs ${c.bName} comparison, which is better ${c.aName} or ${c.bName}, ${c.title.toLowerCase()}, ${c.aName.toLowerCase()} requirements, ${c.bName.toLowerCase()} requirements, ${c.aName.toLowerCase()} qualification, ${c.bName.toLowerCase()} qualification, best loan for investors, investor loan comparison ${params.slug.replace(/-/g, " ")}`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Compare", url: "/compare" },
        { name: c.title, url },
      ],
      service: { name: c.title },
      faqs: c.faqs,
    });
  },
  component: ComparePage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs><Section><p className="py-16 text-center">Comparison not found. <Link to="/compare" className="text-brand underline">See all comparisons</Link>.</p></Section></SiteShell>
  ),
});

function ComparePage() {
  const { comparison: c } = Route.useLoaderData();
  return (
    <SiteShell hideBreadcrumbs>
      <PageHero eyebrow="Compare" title={c.title} subtitle={c.intro} />
      <Section>
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Compare", href: "/compare" }, { name: c.title }]} />
        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="p-3 font-semibold">Attribute</th>
                    <th className="p-3 font-semibold">{c.aName}</th>
                    <th className="p-3 font-semibold">{c.bName}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.rows.map((r: { attribute: string; a: string; b: string }) => (
                    <tr key={r.attribute} className="border-t border-border">
                      <td className="p-3 font-medium">{r.attribute}</td>
                      <td className="p-3 text-muted-foreground">{r.a}</td>
                      <td className="p-3 text-muted-foreground">{r.b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Best for {c.aName}</h3>
                <ul className="mt-3 space-y-1 text-sm text-muted-foreground">{c.bestFor.a.map((x: string) => <li key={x}>• {x}</li>)}</ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Best for {c.bName}</h3>
                <ul className="mt-3 space-y-1 text-sm text-muted-foreground">{c.bestFor.b.map((x: string) => <li key={x}>• {x}</li>)}</ul>
              </div>
            </div>
            {c.slug === "dscr-vs-conventional" && <FannieRentalIncomeUpdateCard />}
            <FAQ items={c.faqs} />
            {c.relatedProducts.length > 0 && (
              <div>
                <h3 className="font-semibold text-brand-dark">Related programs</h3>
                <ul className="mt-2 flex flex-wrap gap-2 text-sm">
                  {c.relatedProducts.map((p: string) => (
                    <li key={p}>
                      <Link to={`/${p}` as string} className="rounded-full border border-brand/30 px-3 py-1 text-brand hover:bg-brand hover:text-brand-foreground">
                        {p.replace(/-/g, " ")}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="space-y-6">
            <div id="lead-form" className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-bold text-foreground">Get pricing</h3>
              <p className="mt-2 text-sm text-muted-foreground">Initial scenario review once we have credit authorization and your supporting documents — usually same day. Formal pricing follows underwriting.</p>
              <Link to="/apply" className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover">Start My Approval</Link>
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold text-brand-dark">Other comparisons</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {comparisons.filter((x) => x.slug !== c.slug).map((x) => (
                  <li key={x.slug}>
                    <Link to="/compare/$slug" params={{ slug: x.slug }} className="text-brand hover:underline">{x.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>
      <CTABand title="Ready to move on your next deal?" body="Complete the short pre-qualification form and upload your supporting documents, and a licensed loan officer will follow up with an initial scenario review." />
      <PageOutro />
    </SiteShell>
  );
}
